import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { userApi } from '@/api/userApi'
import type { User } from '@/api/types'
import { UserAvatar } from '@/components/ui/UserAvatar'

interface UserAutocompleteProps {
  value: User | null
  /** Raw text typed by the user (so the caller can still submit a free-form email). */
  query: string
  onChange: (user: User | null, email: string) => void
  excludeUserIds?: number[]
  placeholder?: string
  disabled?: boolean
  autoFocus?: boolean
}

export function UserAutocomplete({
  value,
  query,
  onChange,
  excludeUserIds,
  placeholder,
  disabled = false,
  autoFocus = false,
}: UserAutocompleteProps) {
  const { t } = useTranslation()
  const [results, setResults] = useState<User[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; width: number } | null>(null)

  useEffect(() => {
    if (!open) return
    function handlePointerDown(e: MouseEvent) {
      const target = e.target as Node
      if (!rootRef.current?.contains(target) && !menuRef.current?.contains(target)) setOpen(false)
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [open])

  useEffect(() => {
    if (!open) return

    const updateMenuPosition = () => {
      const rect = rootRef.current?.getBoundingClientRect()
      if (!rect) return
      const width = rect.width
      setMenuPosition({
        top: rect.bottom + 4,
        left: Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - width - 8)),
        width,
      })
    }

    updateMenuPosition()
    window.addEventListener('resize', updateMenuPosition)
    window.addEventListener('scroll', updateMenuPosition, true)
    return () => {
      window.removeEventListener('resize', updateMenuPosition)
      window.removeEventListener('scroll', updateMenuPosition, true)
    }
  }, [open])

  useEffect(() => {
    const q = query.trim()
    if (q.length < 2) {
      setResults([])
      setOpen(false)
      return
    }
    if (value && value.email.toLowerCase() === q.toLowerCase()) {
      setOpen(false)
      return
    }

    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      void (async () => {
        setLoading(true)
        try {
          const page = await userApi.search({ q, limit: 10 })
          const exclude = new Set(excludeUserIds ?? [])
          setResults(page.results.filter((u) => !exclude.has(u.id)))
          setOpen(true)
        } catch {
          /* network error — swallow */
        } finally {
          setLoading(false)
        }
      })()
    }, 300)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [query, value, excludeUserIds])

  function handleSelect(user: User) {
    onChange(user, user.email)
    setOpen(false)
  }

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <input
        type="email"
        value={query}
        onChange={(e) => onChange(null, e.target.value)}
        placeholder={placeholder ?? t('members.invite.emailPlaceholder', 'user@example.com')}
        className="morph-input"
        disabled={disabled}
        autoComplete="off"
        autoFocus={autoFocus}
        style={{ width: '100%' }}
      />
      {loading && (
        <span
          style={{
            position: 'absolute',
            right: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 10,
            opacity: 0.5,
          }}
        >
          ...
        </span>
      )}

      {open && menuPosition && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: menuPosition.top,
            left: menuPosition.left,
            width: menuPosition.width,
            zIndex: 1400,
            background: 'var(--bg-popup)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            overflowY: 'auto',
            maxHeight: 'min(320px, calc(100dvh - 24px))',
          }}
        >
          {results.length === 0 ? (
            <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-dim)' }}>
              {t(
                'members.invite.noUsers',
                'Nenhum usuário compartilhado encontrado. Você ainda pode convidar digitando o e-mail completo.',
              )}
            </div>
          ) : (
            results.map((user) => (
              <button
                key={user.id}
                type="button"
                onClick={() => handleSelect(user)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text)',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
                onMouseEnter={(e) => {
                  ;(e.currentTarget as HTMLElement).style.background = 'rgba(99,102,241,.08)'
                }}
                onMouseLeave={(e) => {
                  ;(e.currentTarget as HTMLElement).style.background = 'transparent'
                }}
              >
                <UserAvatar userId={user.id} name={user.name} email={user.email} size={28} />
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>
                    {user.name || user.email}
                  </div>
                  {user.name && (
                    <div style={{ fontSize: 11, opacity: 0.55 }}>{user.email}</div>
                  )}
                </div>
              </button>
            ))
          )}
        </div>,
        document.body,
      )}
    </div>
  )
}
